import {
  Box,
  CircularProgress,
  Container,
  CssBaseline,
  ThemeProvider,
} from "@mui/material";
import { createTheme } from "@mui/material/styles";
import React, { Suspense, useEffect } from "react";
import { useIdleTimer } from "react-idle-timer";
import { useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes as RouterRoutes,
} from "react-router-dom";
import PermissionProvider from "src/context/PermissionContext";
import "./App.css";
import { ApiResponse } from "./apis";
import { GetCountries } from "./apis/loopupListApis";
import { getUserPermissions } from "./apis/userApis";
import { useAuth } from "./auth/AuthContext";
import { Layout as DashboardLayout } from "./components/index/Layout";
import Authentication from "./pages/Authentication";
import {
  getAuthState,
  getAuthStatus,
  logout,
  setPermissions,
} from "./redux/slices/authSlice";
import { setCountries } from "./redux/slices/lookupListSlice";
import { getSystemState } from "./redux/slices/systemSlice";
import Routes from "./routes";
import { useSnackbar } from "src/context/ShowMessage";
import darkTheme from "./theme/dark/darkTheme";
import lightTheme from "./theme/light/lightTheme";
import { FieldRefsProvider } from "./context/FieldRefsContext";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";

const App: React.FC = () => {
  const isAuthenticated = useSelector(getAuthStatus);
  const { permissions } = useSelector(getAuthState);
  const { displayMessage } = useSnackbar();

  const { theme: currentTheme } = useSelector(getSystemState);

  const dispatch = useDispatch();

  const applicationTheme = createTheme(
    JSON.parse(
      JSON.stringify(currentTheme === "light" ? lightTheme : darkTheme)
    )
  );

  const auth = useAuth();

  // const { userId, clientId } = auth.currentUser;

  const loadLookupList = async () => {
    await GetCountries().then((response) => {
      if (response.status === 200) {
        dispatch(setCountries({ countries: response.data }));
      }
    });
  };

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response: ApiResponse = await getUserPermissions();

        if (response.status === 200) {
          dispatch(setPermissions({ permissions: response.data }));
        }
      } catch (error: any) {
        displayMessage(`${error.message}`, "error");
      }
    };
    if (isAuthenticated) {
      fetchPermissions && fetchPermissions();
      loadLookupList();
    }
  }, [isAuthenticated]);

  //#region logout
  const tokenKey = `${process.env.REACT_APP_JWT_TOKEN_KEY}`;

  function signOutUser() {
    dispatch(logout());
    tokenKey && localStorage.removeItem(tokenKey);
    window.location.href = "/login";
  }

  const onIdle = () => {
    signOutUser();
  };

  useIdleTimer({
    timeout: 60 * 60 * 1000,
    onIdle,
  });
  //#endregion logout

  const Loader = () => {
    return (
      <Box
        width="100%"
        height="calc(100vh - 64px)"
        display="flex"
        justifyContent="center"
        alignItems="center"
        bgcolor="neutral.300"
      >
        <CircularProgress color="primary" variant="indeterminate" size={120} />
      </Box>
    );
  };

  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider theme={applicationTheme}>
        <CssBaseline />
        <PermissionProvider permissions={permissions}>
          <FieldRefsProvider>
            <div className="App">
              <Router>
                <RouterRoutes>
                  {!isAuthenticated ? (
                    <Route path="/login" element={<Authentication />} />
                  ) : (
                    ""
                  )}
                  <Route
                    path="/*"
                    element={
                      !isAuthenticated ? (
                        <Navigate to="/login" />
                      ) : (
                        <DashboardLayout>
                          <Suspense fallback={<Loader />}>
                            <Container maxWidth={false} sx={{ py: 2 }}>
                              <Routes />
                            </Container>
                          </Suspense>
                        </DashboardLayout>
                      )
                    }
                  />
                </RouterRoutes>
              </Router>
            </div>
          </FieldRefsProvider>
        </PermissionProvider>
      </ThemeProvider>
    </I18nextProvider>
  );
};

export default App;
